<script setup lang="ts">
import ConfirmDlg from '@/components/confirm-dlg.vue'
import { Adresse, AdresseTyp } from '@/models/Adresse'
import KpTextfield from '@/components/kp-textfield.vue'
import { computed, onMounted, ref, useTemplateRef, type Ref } from 'vue'
import { GesuchStatus, GesuchPhase, type Gesuch } from '@/models/Gesuch'

import { useAuthUser } from '@/helpers/globals'
import { instance as api, buildUrl } from '@/helpers/api'
import { errToStr } from '@/helpers/tools'
import { useToast } from '@/composables/toast'
import { createModel } from '@kp/js-model'
import { verifyUser } from '@/helpers/auth'
import { useI18n } from 'vue-i18n'
import type { StepType } from './GesuchPanel.vue'
import { useDisplay } from 'vuetify'

type ApollonValidationError = {
    text: string
    type:
        | 'missing_address'
        | 'missing_address_field'
        | 'missing_address_link'
        | 'missing_liegenschaft'
        | 'field_value'
        | 'missing_document'
        | 'invalid_account_data'
    context?: string
}

type ValidationError = {
    errorGroup: 'Adressen' | 'Liegenschaft' | 'Projekt' | 'Dokumente' | 'Kontoinformation'
    errors: ApollonValidationError[]
    liegenschaft_id?: number
}

const { gesuch, loading, readOnly } = defineProps<{
    gesuch: Gesuch | null
    loading?: boolean
    readOnly?: boolean
}>()

const checkLoading = ref(false)
const checkErrors: Ref<ValidationError[]> = ref([])
const checkSuccess = ref(false)
const password: Ref<string | null> = ref(null)
const passwordError: Ref<string | null> = ref(null)
const verifyLoading = ref(false)
const selectedAddress: Ref<{ id: number | null; type: AdresseTyp } | null> = ref(null)
const delegates: Ref<{ text: string; value: { id: number | null; type: AdresseTyp } }[]> = ref([])
const confirmDelegateDlg = ref(false)
const eigentuemerAdresse: Ref<Adresse | null> = ref(null)
const bevollmaechtigterAdresse: Ref<Adresse | null> = ref(null)
const showEinreichung = ref(false)
const informAddresses: Ref<{ id: number | null; type: AdresseTyp }[]> = ref([])
const informAddressesCommit: Ref<{ id: number | null; type: string }[]> = ref([])
const delegateAddress: Ref<{ id: number | null; type: AdresseTyp } | null> = ref(null)
const acceptFoerderbedingungen = ref(false)
const formvalid = ref(false)
const saving = ref(false)
const delegating = ref(false)
const foerderbedingungenValid = ref(true)

const benutzer = useAuthUser()
const toast = useToast()
const { t } = useI18n()
const { xs: isXs } = useDisplay()

const commitform = useTemplateRef('commitform')

const emit = defineEmits<{
    (e: 'next', type: StepType | null): void
}>()

const isOwner = computed(() => {
    let res = false
    if (eigentuemerAdresse.value) {
        res = benutzer?.id === eigentuemerAdresse.value.benutzer_id
        if (Array.isArray(benutzer?.team_mitglieder)) {
            if (eigentuemerAdresse.value?.benutzer_id) {
                res =
                    res || benutzer?.team_mitglieder.includes(eigentuemerAdresse.value.benutzer_id)
            }
        }
    }
    return res
})

const isRepresentative = computed(() => {
    let res = false
    if (bevollmaechtigterAdresse.value) {
        res = benutzer?.id === bevollmaechtigterAdresse.value.benutzer_id
        if (Array.isArray(benutzer?.team_mitglieder)) {
            if (bevollmaechtigterAdresse.value?.benutzer_id) {
                res =
                    res ||
                    benutzer?.team_mitglieder.includes(bevollmaechtigterAdresse.value.benutzer_id)
            }
        }
    }
    return res
})

onMounted(() => {
    getDelegateAdresses()
    getEigentuemerAdresse()
    getBevollmaechtigterAdresse()
    showEinreichung.value = true
})

async function verifyGesuch() {
    const res = await api().post('/gesuch/verify', {
        gesuch_id: gesuch?.id,
    })
    return res.data
}

async function getDelegates() {
    const res = await api().post('/gesuch/getDelegates', {
        gesuch_id: gesuch?.id,
    })
    return res.data
}

async function delegateGesuch(
    adresse_type: AdresseTyp | null,
    inform_types: { id: number | null; type: AdresseTyp }[] | null,
) {
    const res = await api().post('/gesuch/delegate', {
        gesuch_id: gesuch?.id,
        adresse_type: adresse_type,
        inform_types: inform_types,
    })
    return res.data
}

async function checkGesuch() {
    checkErrors.value = []
    checkSuccess.value = false
    checkLoading.value = true
    try {
        const result = await verifyGesuch()
        foerderbedingungenValid.value = result.foerderbedingungen
        if (result.error && Object.keys(result.error).length > 0) {
            checkErrors.value = result.error
        } else {
            checkSuccess.value = true
        }
        return result
    } catch (e) {
        toast.add({ text: errToStr(e), color: 'error' })
    } finally {
        checkLoading.value = false
    }
}

async function getDelegateAdresses() {
    if (!isOwner.value) {
        const deles = await getDelegates()
        Object.keys(deles.adressen).forEach((d) => {
            const adr = createModel(Adresse, deles.adressen[d].data)
            let name = ''
            if (adr.typ === AdresseTyp.ORGANISATION) {
                name += adr.organisation_name
                if (adr.nachname || adr.vorname) {
                    name += ', ' + (adr.nachname || '') + ' ' + (adr.vorname || '')
                }
            } else {
                name = (adr.nachname || '') + ' ' + (adr.vorname || '')
            }
            delegates.value.push({
                text: deles.adressen[d].label + ': ' + name,
                value: {
                    id: adr.id,
                    type: d as AdresseTyp,
                },
            })
            informAddressesCommit.value.push({
                id: adr.id,
                type: d as AdresseTyp,
            })
        })
    }
}

async function checkPassword() {
    if (commitform.value?.validate()) {
        verifyLoading.value = true
        passwordError.value = null
        const verified = await verifyUser(password.value || '')
        if (verified) {
            abschluss()
        } else {
            passwordError.value = t('wrong_password')
        }
        verifyLoading.value = false
    }
}

async function commitGesuch() {
    const res = await api().post('/gesuch/commit', {
        gesuch_id: gesuch?.id,
        inform_types: informAddressesCommit.value,
    })
    return res.data
}

async function abschluss() {
    try {
        saving.value = true
        gesuch?.save({ inform_types: informAddressesCommit })
        const result = await commitGesuch()
        if (result.error && Object.keys(result.error).length > 0) {
            toast.add({ text: t('gesuch_check_failed'), color: 'error' })
            checkErrors.value = result.errors
        } else {
            toast.add({ text: t('succuessful_commit'), color: 'success', timeout: 10000 })
            emit('next', null)
        }
    } catch (e) {
        toast.add({ text: errToStr(e), color: 'error' })
    } finally {
        saving.value = false
    }
}

function checkDelegate() {
    if (selectedAddress.value?.type !== AdresseTyp.ORGANISATION && !isOwner.value) {
        confirmDelegateDlg.value = true
    } else {
        delegate()
    }
}

async function delegate() {
    try {
        delegating.value = true
        const result = await delegateGesuch(
            selectedAddress.value?.type || null,
            informAddresses.value,
        )
        if (result.error && Object.keys(result.error).length > 0) {
            toast.add({ text: t('gesuch_check_failed'), color: 'error' })
            checkErrors.value = result.errors
        } else {
            toast.add({ text: t('succuessful_delegation'), color: 'success' })
            emit('next', null)
        }
    } catch (e) {
        toast.add({ text: errToStr(e), color: 'error' })
    } finally {
        confirmDelegateDlg.value = false
        delegating.value = false
    }
}

function getEigentuemerAdresse() {
    eigentuemerAdresse.value = gesuch?.data?.adressen?.eigentuemer?.adresse
        ? gesuch.data.adressen.eigentuemer.adresse
        : null
}

async function getBevollmaechtigterAdresse() {
    bevollmaechtigterAdresse.value = gesuch?.data?.adressen?.bevollmaechtigter?.adresse
        ? gesuch.data.adressen.bevollmaechtigter.adresse
        : null
}

function onDelegateAddressSelected(item: { id: number | null; type: AdresseTyp }) {
    delegateAddress.value = item
    informAddresses.value.push(item)
}

function downloadpdf() {
    const url = buildUrl('/gesuch/downloadSummary', {
        gesuch_id: gesuch?.id,
    })
    window.open(url, 'apollon-dl')
}
</script>

<template>
    <v-container fluid>
        <v-card class="mb-2 centered-card pa-4">
            <v-card-title>{{ $t('gesuch_summary') }}</v-card-title>
            <v-card-text>
                <v-btn
                    :disabled="loading"
                    @click="downloadpdf"
                    variant="outlined"
                    prepend-icon="fas fa-download"
                >
                    {{ $t('download_pdf') }}
                </v-btn>
            </v-card-text>
        </v-card>
        <v-card v-if="!readOnly" class="mb-2 centered-card pa-4">
            <v-card-title>
                {{
                    gesuch?.phase === GesuchPhase.EINREICHUNG
                        ? $t('check_gesuch')
                        : $t('check_abschluss')
                }}
            </v-card-title>
            <v-card-text>
                <v-btn
                    v-if="!checkSuccess"
                    variant="flat"
                    color="action white--text"
                    :loading="checkLoading"
                    class="mb-2"
                    prepend-icon="fas fa-tasks"
                    @click="checkGesuch"
                >
                    {{
                        gesuch?.phase === GesuchPhase.EINREICHUNG
                            ? $t('check_gesuch')
                            : $t('check_abschluss')
                    }}
                </v-btn>
                <template
                    v-if="
                        Object.keys(checkErrors).length === 0 &&
                        checkSuccess &&
                        foerderbedingungenValid
                    "
                >
                    <v-alert type="success" border="start" colored-border variant="outlined">
                        {{
                            gesuch?.phase === GesuchPhase.EINREICHUNG
                                ? $t('gesuch_complete_msg')
                                : $t('abschluss_complete_msg')
                        }}
                    </v-alert>
                </template>
                <template
                    v-if="
                        !foerderbedingungenValid &&
                        (gesuch?.status === GesuchStatus.NEW ||
                            gesuch?.status === GesuchStatus.READY_FOR_SUBMIT)
                    "
                >
                    <v-alert type="error" border="start" colored-border variant="outlined">
                        <div v-html="$t('foerderbedingungen_changed')"></div>
                    </v-alert>
                </template>
                <template v-if="Object.keys(checkErrors).length > 0">
                    <v-alert type="error" border-color="error" border="start" variant="outlined">
                        {{ $t('existing_errors_msg') }}:
                        <v-list v-for="(error, key) in checkErrors" :key="key" dense>
                            <v-list-subheader>
                                {{ error.errorGroup }}
                                <!-- Add navigation buttons, depending on the error group -->
                                <v-btn
                                    v-if="error.liegenschaft_id"
                                    size="small"
                                    icon="fas fa-arrow-circle-right"
                                    variant="plain"
                                    color="info"
                                    :loading="verifyLoading || loading || saving"
                                    :to="{
                                        name: 'gesuch-step',
                                        params: {
                                            nr: gesuch?.nummer,
                                            step: 'liegenschaften',
                                            edit_id: error.liegenschaft_id,
                                        },
                                    }"
                                >
                                </v-btn>
                                <v-btn
                                    v-if="error.errorGroup === 'Liegenschaft'"
                                    size="small"
                                    icon="fas fa-arrow-circle-right"
                                    variant="plain"
                                    color="info"
                                    :loading="verifyLoading || loading || saving"
                                    :to="{
                                        name: 'gesuch-step',
                                        params: {
                                            nr: gesuch?.nummer,
                                            step: 'liegenschaften',
                                        },
                                    }"
                                >
                                </v-btn>
                                <v-btn
                                    v-if="error.errorGroup === 'Projekt'"
                                    size="small"
                                    icon="fas fa-arrow-circle-right"
                                    variant="plain"
                                    color="info"
                                    :loading="verifyLoading || loading || saving"
                                    :to="{
                                        name: 'gesuch-step',
                                        params: {
                                            nr: gesuch?.nummer,
                                            step: 'projekt',
                                        },
                                    }"
                                >
                                </v-btn>
                                <v-btn
                                    v-if="error.errorGroup === 'Dokumente'"
                                    size="small"
                                    icon="fas fa-arrow-circle-right"
                                    variant="plain"
                                    color="info"
                                    :loading="verifyLoading || loading || saving"
                                    :to="{
                                        name: 'gesuch-step',
                                        params: {
                                            nr: gesuch?.nummer,
                                            step: 'dokumente',
                                        },
                                    }"
                                >
                                </v-btn>
                                <v-btn
                                    v-if="error.errorGroup === 'Kontoinformation'"
                                    size="small"
                                    icon="fas fa-arrow-circle-right"
                                    variant="plain"
                                    color="info"
                                    :loading="verifyLoading || loading || saving"
                                    :to="{
                                        name: 'gesuch-step',
                                        params: {
                                            nr: gesuch?.nummer,
                                            step: 'kontoinformation',
                                        },
                                    }"
                                >
                                </v-btn>
                            </v-list-subheader>
                            <v-list-item v-for="(e, i) in error.errors" :key="i">
                                {{ e.text }}
                                <v-btn
                                    v-if="e.type === 'missing_address'"
                                    size="small"
                                    icon="fas fa-arrow-circle-right"
                                    variant="plain"
                                    color="info"
                                    :loading="verifyLoading || loading || saving"
                                    :to="{ name: 'gesuch', params: { nr: gesuch?.nummer } }"
                                >
                                </v-btn>
                                <v-btn
                                    v-if="e.type === 'missing_address_field'"
                                    color="info"
                                    size="small"
                                    icon="fas fa-arrow-circle-right"
                                    variant="plain"
                                    :loading="verifyLoading || loading || saving"
                                    :to="{
                                        name: 'gesuch',
                                        params: { nr: gesuch?.nummer, edit_type: e.context },
                                    }"
                                >
                                </v-btn>
                            </v-list-item>
                        </v-list>
                    </v-alert>
                </template>
            </v-card-text>
        </v-card>
        <v-card
            v-if="showEinreichung && !readOnly && (isOwner || isRepresentative)"
            class="mb-2 centered-card pa-4"
        >
            <v-form ref="commitform" v-model="formvalid" lazy-validation>
                <v-card-title>
                    {{
                        gesuch?.phase === GesuchPhase.EINREICHUNG
                            ? $t('commit_gesuch')
                            : $t('commit_abschluss')
                    }}
                </v-card-title>
                <v-card-text>
                    <v-alert
                        v-if="!checkSuccess"
                        type="error"
                        border="start"
                        colored-border
                        variant="outlined"
                    >
                        {{
                            gesuch?.phase === GesuchPhase.EINREICHUNG
                                ? $t('gesuch_commit_msg')
                                : $t('abschluss_commit_msg')
                        }}
                    </v-alert>
                    <template v-if="checkSuccess">
                        <v-alert type="warning" border="start" colored-border variant="outlined">
                            {{ $t('no_edit_after_commit_msg') }}
                        </v-alert>
                        <div class="mb-2 mt-4">
                            <strong>
                                {{
                                    gesuch?.phase === GesuchPhase.EINREICHUNG
                                        ? $t('gesuch_password_confirm_msg')
                                        : $t('abschluss_password_confirm_msg')
                                }}
                            </strong>
                        </div>
                        <kp-textfield
                            v-model="password"
                            :error-messages="passwordError"
                            icon-type="password"
                            :label="$t('pw')"
                            @focus="passwordError = null"
                        />
                    </template>
                </v-card-text>
                <v-card-actions :class="isXs ? 'xs-card-action' : ''">
                    <v-checkbox
                        v-model="acceptFoerderbedingungen"
                        :label="$t('foerderbedingungen_accepted')"
                        :rules="[(v) => !!v || $t('accept_foerderbedingungen')]"
                        required
                    ></v-checkbox>
                    <v-spacer />
                    <v-btn
                        color="action white--text"
                        variant="flat"
                        :disabled="!checkSuccess || !acceptFoerderbedingungen"
                        :loading="verifyLoading || loading || saving"
                        @click="checkPassword"
                        prepend-icon="fas fa-paper-plane-top"
                    >
                        {{
                            gesuch?.phase === GesuchPhase.EINREICHUNG
                                ? $t('commit_gesuch')
                                : $t('commit_abschluss')
                        }}
                    </v-btn>
                </v-card-actions>
            </v-form>
        </v-card>
        <v-card v-if="showEinreichung && !readOnly" class="centered-card pa-4">
            <v-card-title v-if="isOwner">{{ $t('delegate_gesuch_for_edit') }}</v-card-title>
            <v-card-title v-else>{{ $t('delegate_gesuch_for_commit') }}</v-card-title>
            <v-card-subtitle>
                <div v-if="isOwner" style="text-wrap: auto">
                    {{ $t('delegate_explanation') }}
                </div>
                <div style="text-wrap: auto">
                    {{ $t('delegate_email_warning') }}
                </div>
            </v-card-subtitle>
            <v-card-text>
                <v-alert
                    v-if="!checkSuccess"
                    type="warning"
                    border="start"
                    colored-border
                    variant="outlined"
                >
                    {{ $t('gesuch_check_msg') }}
                </v-alert>
                <div class="mt-4">
                    <div class="text-subtitle-1 text-medium-emphasis">
                        {{ $t('delegate_to') }}
                    </div>
                    <v-select
                        class="inputfield"
                        v-model:model-value="selectedAddress"
                        :items="delegates"
                        item-title="text"
                        item-value="value"
                        style="max-width: 400px"
                        clearable
                        clear-icon="fas fa-times"
                        @update:modelValue="onDelegateAddressSelected"
                    >
                        <template v-slot:prepend-inner>
                            <v-icon class="icon-margin" size="x-small"
                                >far fa-hand-point-right</v-icon
                            >
                        </template>
                    </v-select>
                </div>
                <div v-if="checkSuccess && delegates?.length > 0">
                    <div class="mt-4 text-subtitle-1 text-medium-emphasis">
                        {{ $t('inform_people') }}
                    </div>
                    <template v-for="(d, key) in delegates" :key="key">
                        <v-checkbox
                            style="margin-left: -0.5rem"
                            v-model:model-value="informAddresses"
                            dense
                            :readonly="delegateAddress ? d.value === delegateAddress : false"
                            :label="d.text"
                            :value="d.value"
                        ></v-checkbox>
                    </template>
                </div>
            </v-card-text>
            <v-card-actions>
                <v-spacer />
                <v-btn
                    color="action white--text"
                    variant="flat"
                    :disabled="!selectedAddress"
                    :loading="verifyLoading || loading || delegating"
                    @click="checkDelegate"
                    prepend-icon="fas fa-share"
                >
                    {{ $t('gesuch_pass') }}
                </v-btn>
            </v-card-actions>
        </v-card>
        <ConfirmDlg
            v-model:model-value="confirmDelegateDlg"
            :title="$t('bestaetigung')"
            :text="$t('delegate_gesuch_msg')"
            :yes-text="$t('next')"
            :no-text="$t('cancel')"
            @yes="delegate"
        />
    </v-container>
</template>

<style lang="css" scoped>
.inputfield {
    margin-left: -1rem;
}
.icon-margin {
    margin-left: 0.25rem;
}
.xs-card-action {
    flex-direction: column;
    align-items: flex-end;
}
</style>
